import React, { useMemo, useCallback, useContext, useEffect } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import Button from '@prisjakt/ui-button';
import { Floater, Alignment } from '@prisjakt/ui/floater';
import ChevronUp from 'mdi-react/ChevronUpIcon';
import ChevronDown from 'mdi-react/ChevronDownIcon';
import { NudgeUp } from '@prisjakt/ui/hideable/variants';
import RouteContext from '../../../../routes/context';
import { AggregationSection } from '../../../../types';
import { useAggregationSettings } from '../../hooks';
import FilterSection, {
  isDisabledSection,
  isValidSection,
} from '../filter-section';
import * as S from './styles';
import { ProductCollectionNonNameSearchFilter } from '../filter/types';

type Props = {
  id: string;
  section: AggregationSection;
  isOpen?: boolean;
  onToggle?: (id: string) => void;
  clearFilter: (property?: string) => void;
  appliedFilters?: ProductCollectionNonNameSearchFilter[];
};

const FeaturedFilter = ({
  id,
  section,
  isOpen,
  onToggle,
  clearFilter,
  appliedFilters,
}: Props) => {
  const { t } = useContext(AppContext);
  const { dispatch, getState } = useContext(RouteContext);
  const { filters } = getState();
  const onToggleHandler = useCallback(
    () => typeof onToggle === 'function' && onToggle(section.id),
    [onToggle, section.id]
  );
  const disabled = useMemo(() => isDisabledSection(section), [section]);

  const icon = useMemo(() => (isOpen ? <ChevronUp /> : <ChevronDown />), [
    isOpen,
  ]);

  const aggregationSettings = useAggregationSettings(id);
  const existingFilter =
    section.property === undefined ||
    Boolean(
      filters?.find((filter: any) => filter.property === section.property)
    );

  // Reset search when closing a quick filter popup
  useEffect(() => {
    if (!isOpen && aggregationSettings?.query) {
      dispatch({
        type: 'AGGREGATION_SEARCH',
        id,
        query: '',
      });
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  return isValidSection(section) ? (
    <S.FilterWrapper
      isOpen={!!isOpen}
      data-test="FeaturedFilter"
      data-test-type={section.__typename}
      data-test-property={section.property}
    >
      <Button
        size="small"
        color="primary"
        variant="text"
        active={!!isOpen}
        icon={icon}
        iconPosition="right"
        onClick={onToggleHandler}
        data-test="FeaturedFilterButton"
        disabled={disabled}
      >
        <S.FeaturedFilterButtonText>{section.title}</S.FeaturedFilterButtonText>
      </Button>
      <Floater
        horizontalAlign={Alignment.Start}
        verticalAlign={Alignment.EndOutside}
      >
        <NudgeUp visible={isOpen}>
          <S.FilterPopup>
            <S.FilterContent>
              <FilterSection
                section={section}
                useInfiniteScroll
                appliedFilters={appliedFilters}
                autoFocusSearch
              />
            </S.FilterContent>
            <S.FilterFooter>
              <S.FilterFooterClearButton
                clearFilter={clearFilter}
                property={section.property}
                disabled={!existingFilter}
                variant="outlined"
                color="primary"
              />
              <S.FilterFooterApplyButton
                color="primary"
                onClick={onToggleHandler}
                disabled={!existingFilter}
              >
                {t('filter', 'button.apply-filter')}
              </S.FilterFooterApplyButton>
            </S.FilterFooter>
          </S.FilterPopup>
        </NudgeUp>
      </Floater>
    </S.FilterWrapper>
  ) : null;
};
export default FeaturedFilter;
